<template>
  <div class="custom-goods-table-wrapper">
    <!-- 批量操作栏 -->
    <div class="batch-operations" v-if="showBatchActions">
      <el-checkbox 
        v-model="selectAll" 
        @change="handleSelectAll"
        :indeterminate="isIndeterminate"
      >全选</el-checkbox>
      <el-button 
        size="mini" 
        type="danger" 
        :disabled="selectedIndices.length === 0"
        @click="batchRemoveGoods"
      >批量删除 ({{ selectedIndices.length }})</el-button>
    </div>

    <!-- 自定义商品列表 -->
    <div class="custom-goods-table">
      <!-- 表头 -->
      <div class="table-header">
        <div class="table-cell checkbox-cell" v-if="showBatchActions">选择</div>
        <div class="table-cell code-cell">商品编码</div>
        <div class="table-cell name-cell">商品名称</div>
        <div class="table-cell market-price-cell">原价</div>
        <div class="table-cell price-cell">成交价</div>
        <div class="table-cell num-cell">数量(不低于最低起订量)</div>
        <div class="table-cell subtotal-cell">小计</div>
        <div class="table-cell operation-cell">操作</div>
      </div>

      <!-- 表格内容 -->
      <div class="table-body">
        <div 
          v-for="(item, index) in tableData" 
          :key="item.productCode || index"
          class="table-row"
          :class="{ 'selected': selectedIndices.includes(index) }"
        >
          <div class="table-cell checkbox-cell" v-if="showBatchActions">
            <el-checkbox 
              v-model="selectedIndices" 
              :label="index"
            >{{ index + 1 }}</el-checkbox>
          </div>
          <div class="table-cell code-cell">
            <span :title="item.productCode">{{ item.productCode }}</span>
          </div>
          <div class="table-cell name-cell">
            <span :title="item.productName">{{ item.productName }}</span>
          </div>
          <div class="table-cell market-price-cell">
            <span class="market-price">{{ parseFloat(item.marketPrice || 0).toFixed(2) }} 元</span>
          </div>
          <div class="table-cell price-cell">
            <span class="discount-price">{{ parseFloat(item.price || 0).toFixed(2) }} 元</span>
          </div>
          <div class="table-cell num-cell">
            <el-input-number
              v-model="item.num"
              :min="item.min_pay_num || 1"
              size="mini"
              style="width: 100%"
              @change="updateSubtotal(index)"
            ></el-input-number>
          </div>
          <div class="table-cell subtotal-cell">
            <span class="subtotal-amount">{{ (parseFloat(item.price || 0) * (item.num || 0)).toFixed(2) }} 元</span>
          </div>
          <div class="table-cell operation-cell">
            <el-button
              size="mini"
              type="text"
              style="color: #ff4757;"
              @click="removeGoods(index)"
            >删除</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="goods-summary">
      <div class="summary-left">
        <span>已选择 {{ tableData.length }} 件商品</span>
        <span v-if="showBatchActions && selectedIndices.length > 0" style="margin-left: 20px;">
          当前选中 {{ selectedIndices.length }} 件
        </span>
      </div>
      <div class="summary-right">
        <span class="total-amount">订单总额: {{ totalAmount.toFixed(2) }} 元</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomGoodsTable',
  props: {
    // 表格数据
    tableData: {
      type: Array,
      default: () => []
    },
    // 是否显示批量操作
    showBatchActions: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      selectedIndices: [], // 选中的商品索引数组
      selectAll: false // 全选状态
    };
  },
  computed: {
    // 计算订单总金额
    totalAmount() {
      return this.tableData.reduce((total, item) => {
        return total + parseFloat(item.price || 0) * (item.num || 0);
      }, 0);
    },

    // 计算是否为半选状态
    isIndeterminate() {
      const selectedCount = this.selectedIndices.length;
      const totalCount = this.tableData.length;
      return selectedCount > 0 && selectedCount < totalCount;
    }
  },
  watch: {
    // 监听选中索引变化，更新全选状态
    selectedIndices: {
      handler() {
        this.updateSelectAllState();
      },
      deep: true
    },
    // 监听表格数据变化，重置选中状态
    tableData: {
      handler() {
        this.resetSelection();
      },
      deep: true
    }
  },
  methods: {
    /* 更新小计 */
    updateSubtotal(index) {
      // 当数量变化时，会自动重新计算totalAmount（computed属性）
      this.$emit('update-subtotal', index, this.tableData[index]);
      this.$forceUpdate(); // 强制更新视图
    },

    /* 全选/取消全选 */
    handleSelectAll(val) {
      if (val) {
        // 全选
        this.selectedIndices = this.tableData.map((item, index) => index);
      } else {
        // 取消全选
        this.selectedIndices = [];
      }
    },

    /* 批量删除商品 */
    batchRemoveGoods() {
      if (this.selectedIndices.length === 0) {
        this.$message.warning("请先选择要删除的商品");
        return;
      }

      this.$confirm(`确定要删除选中的 ${this.selectedIndices.length} 个商品吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        // 按索引倒序排列，从后往前删除，避免索引错乱
        const sortedIndices = [...this.selectedIndices].sort((a, b) => b - a);
        
        // 发送批量删除事件
        this.$emit('batch-remove', sortedIndices);
        
        // 清空选中状态
        this.selectedIndices = [];
        this.selectAll = false;
        
        this.$message.success(`成功删除 ${sortedIndices.length} 个商品`);
      }).catch(() => {
        // 用户取消删除
      });
    },

    /* 单个删除商品 */
    removeGoods(index) {
      this.$emit('remove-goods', index);
      // 删除后需要更新选中状态
      this.updateSelectedIndicesAfterRemove(index);
    },

    /* 删除商品后更新选中索引 */
    updateSelectedIndicesAfterRemove(removedIndex) {
      // 移除对应的选中索引
      const indexInSelected = this.selectedIndices.indexOf(removedIndex);
      if (indexInSelected > -1) {
        this.selectedIndices.splice(indexInSelected, 1);
      }
      
      // 更新其他选中索引（大于被删除索引的需要减1）
      this.selectedIndices = this.selectedIndices.map(index => 
        index > removedIndex ? index - 1 : index
      );
      
      // 更新全选状态
      this.updateSelectAllState();
    },

    /* 更新全选状态 */
    updateSelectAllState() {
      const selectedCount = this.selectedIndices.length;
      const totalCount = this.tableData.length;
      this.selectAll = selectedCount === totalCount && totalCount > 0;
    },

    /* 重置选中状态 */
    resetSelection() {
      this.selectedIndices = [];
      this.selectAll = false;
    },

    /* 获取选中的商品 */
    getSelectedGoods() {
      return this.selectedIndices.map(index => this.tableData[index]);
    },

    /* 获取选中的索引 */
    getSelectedIndices() {
      return [...this.selectedIndices];
    }
  }
};
</script>

<style scoped lang="less">
.custom-goods-table-wrapper {
  width: 100%;
}

// 批量操作栏样式
.batch-operations {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 15px;

  .el-checkbox {
    margin-right: 10px;
  }
}

// 自定义商品表格样式
.custom-goods-table {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;

  .table-header {
    display: flex;
    background-color: #f5f7fa;
    border-bottom: 1px solid #ebeef5;
    font-weight: 600;
    color: #606266;

    .table-cell {
      padding: 12px 8px;
      text-align: center;
      border-right: 1px solid #ebeef5;
      font-size: 14px;

      &:last-child {
        border-right: none;
      }
    }
  }

  .table-body {
    max-height: 500px;
    overflow-y: auto;

    .table-row {
      display: flex;
      border-bottom: 1px solid #ebeef5;
      transition: background-color 0.3s;

      &:hover {
        background-color: #f5f7fa;
      }

      &.selected {
        background-color: #ecf5ff;
      }

      &:last-child {
        border-bottom: none;
      }

      .table-cell {
        padding: 12px 8px;
        text-align: center;
        border-right: 1px solid #ebeef5;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #606266;

        &:last-child {
          border-right: none;
        }

        span {
          word-break: break-word;
          line-height: 1.4;
        }
      }
    }
  }

  // 列宽定义
  .checkbox-cell {
    width: 60px;
    min-width: 60px;
  }

  .code-cell {
    width: 180px;
    min-width: 180px;
    text-align: left !important;
    justify-content: flex-start !important;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  .name-cell {
    flex: 1;
    min-width: 200px;
    text-align: left !important;
    justify-content: flex-start !important;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  .market-price-cell {
    width: 100px;
    min-width: 100px;

    .market-price {
      color: #909399;
      text-decoration: line-through;
      font-size: 13px;
    }
  }

  .price-cell {
    width: 100px;
    min-width: 100px;

    .discount-price {
      color: #f56c6c;
      font-weight: 600;
    }
  }

  .num-cell {
    width: 180px;
    min-width: 180px;
  }

  .subtotal-cell {
    width: 120px;
    min-width: 120px;

    .subtotal-amount {
      color: #e6a23c;
      font-weight: 600;
    }
  }

  .operation-cell {
    width: 80px;
    min-width: 80px;
  }
}

// 统计信息样式
.goods-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-top: 1px solid #ebeef5;
  margin-top: 15px;

  .summary-left {
    color: #909399;
    font-size: 14px;
  }

  .summary-right {
    .total-amount {
      color: #f56c6c;
      font-size: 16px;
      font-weight: 700;
    }
  }
}
</style>
